import React, { useEffect, useState } from "react";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, CircleSlash, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const META = {
  ok: { color: "#22c55e", Icon: CheckCircle2, label: "Healthy" },
  warn: { color: "#eab308", Icon: AlertTriangle, label: "Needs attention" },
  error: { color: "#ef4444", Icon: XCircle, label: "Error" },
  off: { color: "#64748b", Icon: CircleSlash, label: "Not connected" },
  info: { color: "#38bdf8", Icon: AlertTriangle, label: "Info" },
};

// Live operational health of every external integration — true state (quota/test-mode/sandbox),
// not a blanket green "connected". SuperAdmin-only; backend caches ~5 min.
// `compact` renders a collapsible summary strip (used on the Overview tab).
export default function IntegrationHealth({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(!compact);

  const load = async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/provider-health${refresh ? "?refresh=true" : ""}`);
      setData(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { load(false); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] p-4" data-testid="integration-health-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Probing integrations live…
      </div>
    );
  }
  if (!data) return null;
  const c = data.counts || {};
  const providers = data.providers || [];
  const down = providers.filter((p) => p.health === "error" || p.health === "warn");
  const allGood = down.length === 0;

  const Grid = () => (
    <div className="grid sm:grid-cols-2 gap-2">
      {providers.map((p) => {
        const m = META[p.health] || META.off;
        const Icon = m.Icon;
        return (
          <div key={p.key} className="flex items-start gap-2.5 rounded-xl border p-2.5"
            style={{ borderColor: `${m.color}44`, background: `${m.color}0d` }}
            data-testid={`ih-provider-${p.key}`}>
            <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: m.color }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold truncate">{p.name}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0"
                  style={{ color: m.color, background: `${m.color}1f` }} data-testid={`ih-label-${p.key}`}>{p.label}</span>
              </div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wide">{p.category}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{p.detail}</div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const Counts = () => (
    <div className="flex items-center gap-2 text-[11px]">
      {c.ok ? <span className="text-emerald-400" data-testid="ih-count-ok">● {c.ok} healthy</span> : null}
      {c.warn ? <span className="text-amber-400" data-testid="ih-count-warn">● {c.warn} warning</span> : null}
      {c.error ? <span className="text-rose-400" data-testid="ih-count-error">● {c.error} error</span> : null}
      {c.off ? <span className="text-slate-400" data-testid="ih-count-off">● {c.off} off</span> : null}
    </div>
  );

  return (
    <div className="rounded-2xl border bg-[hsl(var(--card))] p-4 mb-4"
      style={{ borderColor: compact && !allGood ? "#ef444455" : "hsl(var(--border))" }}
      data-testid={compact ? "integration-health-strip" : "integration-health"}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          {allGood ? <ShieldCheck className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
          Live Integration Health
        </h3>
        <Counts />
        <div className="ml-auto flex items-center gap-3">
          {compact && (
            <button onClick={() => setOpen((o) => !o)} data-testid="ih-toggle-btn"
              className="flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              {open ? <><ChevronUp className="w-3.5 h-3.5" /> Hide</> : <><ChevronDown className="w-3.5 h-3.5" /> Show all</>}
            </button>
          )}
          <button onClick={() => load(true)} disabled={refreshing} data-testid="ih-refresh-btn"
            className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-probe
          </button>
        </div>
      </div>

      {/* Down-provider alert (prominent on the Overview strip) */}
      {down.length > 0 ? (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-2.5 mb-3" data-testid="ih-down-alert">
          <div className="flex items-center gap-1.5 text-[12px] font-bold text-rose-300 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {down.length} integration{down.length === 1 ? "" : "s"} need attention
          </div>
          <div className="flex flex-wrap gap-1.5">
            {down.map((p) => {
              const m = META[p.health] || META.warn;
              return (
                <span key={p.key} data-testid={`ih-down-${p.key}`}
                  className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ color: m.color, background: `${m.color}1f`, border: `1px solid ${m.color}55` }}>
                  {p.name}: {p.label}
                </span>
              );
            })}
          </div>
        </div>
      ) : (
        compact && <div className="text-[11px] text-emerald-400/90 mb-2" data-testid="ih-all-good">All integrations operational.</div>
      )}

      {open && <Grid />}

      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2">
        Checked {data.checked_at ? new Date(data.checked_at).toLocaleString() : "—"}{data.cached ? " · cached" : ""}
      </div>
    </div>
  );
}
